import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { FaUserGraduate, FaBook, FaScroll } from "react-icons/fa";
import BuscaEmpresa from "../../components/CompanySearch/index.js";
import { API_ENDPOINTS } from "../../../js/api.js";
import "./style.css";

const Dashboard = () => {
  const navigate = useNavigate();

  // Estado da busca interna (só usado quando programa?.isCiee === true).
  const [resultados, setResultados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  // True enquanto há termo digitado na busca. Usado para esconder
  // os cards de seleção de programa enquanto o usuário está buscando
  // (decisão de UX: foca no resultado, evita confusão visual).
  const [buscaAtiva, setBuscaAtiva] = useState(false);

  const getPrograma = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return {
        tipo: Number(decoded.tipo_programa),
        tipoUsuario: decoded.tipo,
        isCiee: decoded.tipo === "usuario_ciee",
        isEstudante: decoded.tipo === "estudante",
        isEmpresa:
          decoded.tipo === "empresa" || decoded.tipo === "usuario_externo",
      };
    } catch (e) {
      return null;
    }
  };

  const programa = getPrograma();

  // Chamada à busca interna do backend.
  // Payload do BuscaEmpresa: { termo, campo, valor, automatico }
  // `campo` é o id do filtro selecionado (ou auto-detectado pelo componente).
  //
  // Estabilizada com useCallback pra não recriar a referência a cada
  // render — o BuscaEmpresa usa `onBuscar` como dependência de useEffect
  // (debounce), e mudanças de identidade fariam a busca disparar em
  // loop infinito.
  const handleBuscar = useCallback(async ({ campo, valor, termo }) => {
    // Se o termo virou vazio (limpar), zera estado e sai cedo.
    if (!termo || !termo.trim() || !valor || !String(valor).trim()) {
      setResultados(null);
      setErro(null);
      setCarregando(false);
      return;
    }

    setCarregando(true);
    setErro(null);
    try {
      const token = localStorage.getItem("token");
      const url = API_ENDPOINTS.empresa_buscar_interno(campo, valor);
      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (resp.status === 404) {
        setResultados([]);
        setCarregando(false);
        return;
      }

      if (!resp.ok) {
        throw new Error(`Falha na busca (HTTP ${resp.status})`);
      }

      const json = await resp.json();
      const lista = Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json)
          ? json
          : [];
      setResultados(lista);
    } catch (e) {
      console.error("[Dashboard] Erro na busca interna:", e);
      setErro(e.message || "Erro ao buscar empresa");
      setResultados(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  const handleSelecionarFormulario = useCallback(
    ({ url, empresa }) => {
      if (!url) return;

      let finalUrl = url;
      // Adiciona o parâmetro empresaId na query string para que o formulário
      // consiga pré-preencher os dados da empresa.
      if (empresa && empresa.id && !finalUrl.includes("empresaId=")) {
        const separator = finalUrl.includes("?") ? "&" : "?";
        finalUrl = `${finalUrl}${separator}empresaId=${empresa.id}`;
      }

      navigate(finalUrl);
    },
    [navigate],
  );

  // Callback chamado pelo BuscaEmpresa a cada keystroke. Atualiza o
  // estado `buscaAtiva` que controla a visibilidade dos cards de
  // programa. Estabilizada com useCallback pra não causar loop no
  // useEffect interno do BuscaEmpresa (que tem [termo] como dep).
  const handleTermoChange = useCallback((novoTermo) => {
    setBuscaAtiva(Boolean(novoTermo && novoTermo.trim()));
  }, []);

  const renderCards = () => {
    if (programa?.isCiee) {
      return (
        <>
          <div className="card-programa" onClick={() => navigate("/tce")}>
            <FaUserGraduate size={50} />
            <h3>Estágio (TCE)</h3>
            <p>Termo de Compromisso de Estágio</p>
          </div>
          <div className="card-programa" onClick={() => navigate("/tca")}>
            <FaBook size={50} />
            <h3>Aprendizagem (TCA)</h3>
            <p>Termo de Compromisso de Aprendizagem Empresa Empregadora</p>
          </div>
          <div
            className="card-programa"
            onClick={() => navigate("/tca-empregador")}
          >
            <FaScroll size={50} />
            <h3>Aprendizagem (TCA)</h3>
            <p>Termo de Compromisso de Aprendizagem CIEEMG Empregador</p>
          </div>
        </>
      );
    }

    if (programa?.isEstudante) {
      return (
        <div className="card-programa" onClick={() => navigate("/tca")}>
          <FaBook size={50} />
          <h3>Aprendizagem (TCA)</h3>
          <p>Termo de Compromisso de Aprendizagem</p>
        </div>
      );
    }

    if (programa?.isEmpresa) {
      if (programa.tipo === 0) {
        return (
          <div className="card-programa" onClick={() => navigate("/tce")}>
            <FaUserGraduate size={50} />
            <h3>Estágio (TCE)</h3>
            <p>Termo de Compromisso de Estágio</p>
          </div>
        );
      }
      if (programa.tipo === 1 || programa.tipo === 2) {
        return (
          <div className="card-programa" onClick={() => navigate("/tca")}>
            <FaBook size={50} />
            <h3>Aprendizagem (TCA)</h3>
            <p>Termo de Compromisso de Aprendizagem</p>
          </div>
        );
      }
      return <p>Nenhum programa vinculado à sua empresa.</p>;
    }

    return <p>Nenhum programa vinculado.</p>;
  };

  return (
    <div
      className={`dashboard-container${
        buscaAtiva ? " dashboard-container--busca-ativa" : ""
      }`}
    >
      <h1>Bem-vindo!</h1>
      <p>Selecione o programa desejado:</p>

      {/* Busca interna exclusiva para colaboradores CIEE
          (SEOPE / SECEX / INFOR — acesso_autorizacao 2/3/4).
          Perfis empresa (0) e estudante (1) NÃO veem este bloco. */}
      {programa?.isCiee && (
        <div className="dashboard-busca-interna">
          <BuscaEmpresa
            onBuscar={handleBuscar}
            onSelecionarFormulario={handleSelecionarFormulario}
            onTermoChange={handleTermoChange}
            resultados={resultados}
            carregando={carregando}
            erro={erro}
          />
        </div>
      )}

      <div className="cards-programa">{renderCards()}</div>
    </div>
  );
};

export default Dashboard;
